import { Button, Dialog, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { FileUploader } from "react-drag-drop-files";
import React, { useEffect, useState } from "react";
import MuiAlert from "@mui/material/Alert";
import Slide from "@mui/material/Slide";
import useCreate from "../../../hooks/useCreate";
import useUploadMedia from "../../../hooks/useUploadMedia";
import { toast } from "react-hot-toast";
import Spinner from "../../../components/spinner";

const fileTypes = ["JPG", "PNG", "JPEG", "WEBP"];

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function EditAttribute({ open, onClose, editData }) {
  const initialState = {
    attribute_value_id: "",
    attribute_value_name: "",
    attribute_value: "",
    icon_url: "",
    image_preview: "",
  };
  const [attributeData, setAttributeData] = useState(initialState);
  const [isError, setIsError] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [imageChanged, setImageChanged] = useState(false);

  const handleInputData = (e) => {
    let value = e.target.value;
    let data = {
      ...attributeData,
      [e.target.name]: value,
    };
    setAttributeData(data);
  };

  const handleuploadAgain = () => {
    let data = {
      ...attributeData,
      image_preview: "",
      icon_url: "",
    };
    setAttributeData(data);
    setImageChanged(true);
  };

  const handleMediaUploadSuccess = (data) => {
    setAttributeData((prev) => ({
      ...prev,
      icon_url: data.data.image_url,
      image_preview: data.data.image_url,
    }));
    setIsImageUploading(false);
    toast.success("Image uploaded successfully", {
      position: "top-right",
      duration: 2000,
    });
  };

  const handleMediaUploadError = (error) => {
    setIsImageUploading(false);
    toast.error(error?.response?.data?.message || "Error uploading image", {
      position: "top-right",
      duration: 3000,
    });
  };

  const { upload } = useUploadMedia({
    url: "upload/image",
    onSuccess: handleMediaUploadSuccess,
    onError: handleMediaUploadError,
  });

  const handleAddFromData = (e, file, isFileObject, name) => {
    let fileObject;
    if (file) {
      if (isFileObject) {
        fileObject = e;
      } else {
        fileObject = e.target.files[0];
      }

      setAttributeData((prev) => ({
        ...prev,
        [name]: fileObject,
      }));

      setImageChanged(true);
      setIsImageUploading(true);
      upload.mutate({ thumbnail: fileObject, screen: "ATTRIBUTE" });
    } else {
      let value = e.target.value;
      setAttributeData((prev) => ({
        ...prev,
        [e.target.name]: value,
      }));
    }
  };

  const handleCloseAndReset = () => {
    onClose();
    setIsError(false);
    setAttributeData(initialState);
    setImageChanged(false);
  };

  const handleEditSuccess = () => {
    handleCloseAndReset();
    toast.success("Attribute Updated Successfully", {
      position: "top-right",
      duration: 3000,
    });
  };

  const handleEditError = (error) => {
    toast.error(error?.response?.data?.message || "Error updating attribute", {
      position: "top-right",
      duration: 3000,
    });
  };

  const { setDataToServer: setAttributeDataToServer } = useCreate({
    refreshUrl: "product/attribute-value/list",
    url: "product/attribute-value/update",
    onSuccess: handleEditSuccess,
    onError: handleEditError,
  });

  const handleEditAttribute = (e) => {
    e.preventDefault();

    const payloadData = {
      attribute_value_id: attributeData.attribute_value_id,
      attribute_value_name: attributeData.attribute_value_name,
      attribute_value: attributeData.attribute_value,
      icon_url: attributeData.icon_url || "",
    };

    setAttributeDataToServer.mutate(payloadData);
  };

  const closeErrorMessage = () => {
    setIsError(false);
  };

  useEffect(() => {
    if (editData) {
      setAttributeData({
        attribute_value_id: editData.attribute_value_id,
        attribute_value_name: editData.attribute_value_name,
        attribute_value: editData.attribute_value,
        icon_url: editData.icon_url || "",
        image_preview: editData.icon_url || "",
      });
      setImageChanged(false);
    }
  }, [editData]);

  return (
    <>
      <Dialog
        fullScreen
        maxWidth={"md"}
        open={open}
        onClose={handleCloseAndReset}
        TransitionComponent={Transition}
      >
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center space-x-4 headingBorder__b sticky top-0 z-40 bg-white">
            <div>
              <IconButton onClick={handleCloseAndReset}>
                <ArrowBackIcon />
              </IconButton>
            </div>
            <div>
              <h1 className="heading">Edit Attribute</h1>
            </div>
          </div>
          <div className="form-body py-8">
            <form className="space-y-4" onSubmit={handleEditAttribute}>
              <div>
                <div className="my-7 flex items-center justify-between border-b border-gray-400">
                  <div className="pb-3">
                    <h1 className="font-semibold text-2xl">
                      Attribute Image
                    </h1>
                    <small className="text-red-700">
                      Note: Image to be less than 1mb
                    </small>
                  </div>
                </div>
                <div className="space-y-4">
                  {isImageUploading ? (
                    <div className="text-center py-8">
                      <Spinner />
                      <p className="mt-2">Uploading image...</p>
                    </div>
                  ) : !attributeData?.image_preview ? (
                    <FileUploader
                      name="icon"
                      types={fileTypes}
                      handleChange={(e) =>
                        handleAddFromData(e, true, true, "icon")
                      }
                      classes="file-uploader"
                      hoverTitle="Drop here"
                    />
                  ) : (
                    <div className="border border-dashed border-blue-700 rounded">
                      <div className="w-40 mx-auto py-4 text-center space-y-3">
                        <img src={attributeData?.image_preview} alt="preview" />
                        <Button variant="outlined" onClick={handleuploadAgain}>
                          Re-Upload
                        </Button>
                        {attributeData.icon_url && imageChanged && (
                          <p className="text-green-600 text-xs">
                            Image uploaded ✓
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {isError && (
                <Alert
                  onClose={closeErrorMessage}
                  className="mb-4"
                  severity="error"
                  sx={{ width: "100%" }}
                >
                  {setAttributeDataToServer?.error?.message}, Please try again
                  later
                </Alert>
              )}
              <div className="space-x-4 text-center pb-5">
                {setAttributeDataToServer.isLoading ? (
                  <Spinner />
                ) : (
                  <>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isImageUploading}
                    >
                      Save
                    </Button>
                    <Button variant="outlined" onClick={handleCloseAndReset}>
                      Close
                    </Button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default EditAttribute;
  